import React, { lazy, useMemo } from 'react';
import { Route } from 'react-router-dom-v5-compat';
import ConfigProvider from 'antd/es/config-provider';

import { packageIds } from 'constants/packageIds';
import { OpAuthenticatedRoute } from 'new-components/OpAppScaffold/components/OpAuthenticatedRoute';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import { scopeCodesToDescriptions } from 'utils/scopeDescriptions';
import { OrgIdOutlet } from 'new-components/OpAppScaffold/components/OrgIdOutlet';
import { OpScopeGuard } from 'new-components/OpScopeGuard/OpScopeGuard';
import { OpAppScaffold } from 'new-components/OpAppScaffold/OpAppScaffold';
import type { OrgSwitcherProps } from 'new-components/OrgSwitcher/types';
import { getBasenameForApp } from 'utils/getBasenameForApp';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useThemeObserver } from 'utils/customHooks/useThemeObserver';
import { OpApp } from 'new-components/DLS/OpApp/OpApp';
import { AlertWrapper } from 'containers/AlertWrapper/AlertWrapper';
import { useIsDmpEnabled } from 'utils/customHooks/useIsDmpEnabled';
import { OpTermsGuard } from 'new-components/OpAppScaffold/components/OpTermsGuard';
import { OpWithoutSidebar } from 'new-components/OpAppScaffold/components/OpWithoutSidebar';
import { OpNotificationProvider } from 'new-components/OpAppScaffold/components/OpNotificationProvider';
import { checkMasterModeAccess } from 'new-components/OrgSwitcher/helpers/orgAccess';

import {
  baseRoute,
  partnerBillingRoute,
  partnerCustomersRoute,
  partnerDashboardRoute,
  partnerMailroomRoute,
  partnerRequestsTableRoute,
  partnerStoreRoute,
  partnerSubscriptionRenewalsTableRoute,
  partnerVideoDeploymentsRoute,
  partnerVideoRoute,
  partnerAccessRoute,
  partnerProtectRoute,
  partnerProtectDeploymentsRoute,
  partnerUsersRoute,
  partnerTermsOfServiceRoute,
  partnerBrandingRoute,
  partnerVisitorRoute,
} from './constants/routes';
import {
  homeMenuItem,
  customersMenuItem,
  renewalsMenuItem,
  requestsMenuItem,
  sidebarItems,
  billingMenuItem,
  storeMenuItem,
  mailroomMenuItem,
  videoMenuItem,
  videoDeploymentsMenuItem,
  accessMenuItem,
  protectMenuItem,
  protectDeploymentsMenuItem,
  usersMenuItem,
  visitorMenuItem,
} from './constants/sidebarItems';
import { usePartnerInfo } from './hooks/usePartnerInfo';
import AccessPage from './pages/AccessPage/AccessPage';
import TermsOfServicePage from './pages/TermsOfServicePage/TermsOfServicePage';
import UsersPage from './pages/UsersPage/UsersPage';
import { MigratedInfoModalWrapper } from './components/MigratedInfoModal/MigratedInfoModalWrapper';

import './App.scss';

const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const BillingPage = lazy(() => import('./pages/BillingPage/BillingPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage/CustomersPage'));
const CustomerPage = lazy(() => import('./pages/CustomerPage/CustomerPage'));
const PartnerSubscriptionRenewalsTablePage = lazy(
  () =>
    import('./pages/SubscriptionRenewalsTablePage/SubscriptionRenewalsPage'),
);
const LicenseRequestsPage = lazy(
  () => import('./pages/LicenseRequestsPage/LicenseRequestsPage'),
);
const StoreAccessPage = lazy(
  () => import('./pages/StoreAccessPage/StoreAccessPage'),
);
const MailroomPage = lazy(() => import('./pages/MailroomPage/MailroomPage'));
const VideoPage = lazy(() => import('./pages/VideoPage/VideoPage'));
const VideoDeploymentsPage = lazy(
  () => import('./pages/VideoDeploymentsPage/VideoDeploymentsPage'),
);
const VisitorPage = lazy(() => import('./pages/VisitorPage/VisitorPage'));
const ProtectPage = lazy(() => import('./pages/ProtectPage/ProtectPage'));
const ProtectDeploymentsPage = lazy(
  () => import('./pages/ProtectDeploymentsPage/ProtectDeploymentsPage'),
);

const BrandingPage = lazy(() => import('./pages/BrandingPage/BrandingPage'));

// Gets the correct basename for the app based on the current environment
const basename = getBasenameForApp('partner');

export const OpPartnerAppScaffold = () => {
  const { isVideoPartner, isAccessPartner } = usePartnerInfo();
  const { identityId, token } = useIdentityAndUser();
  const { theme } = useThemeObserver({ identityId });

  const { isDmpEnabled } = useIsDmpEnabled();

  const hasMasterModeAccess = useMemo(
    () =>
      checkMasterModeAccess({
        token,
      }),
    [token],
  );

  /**
   * This is getting the orgs that the identity belongs to from the token scope list as that is what drives
   * the permissions for our Products (or they are a Master mode user). Additionally, it filters only on partner
   * orgs as within the context of the partner app we can only app switch within the app to partner entitled orgs
   */

  const orgsIdentityBelongsTo =
    token?.tokenScopeList?.map((scope) => scope.org?.id).filter(Boolean) || [];

  const orgSwitcherFilter: NonNullable<OrgSwitcherProps['orgDataFilter']> = (
    orgs,
  ) =>
    (orgs ?? []).filter((org) => {
      return (
        (orgsIdentityBelongsTo.includes(org?.id ?? 0) || hasMasterModeAccess) &&
        (org?.enabledPackagePlans ?? []).some((plan) => {
          return [packageIds.subOrgs, packageIds.subOrgsVideo].includes(
            plan?.package?.id ?? 0,
          );
        })
      );
    });

  return (
    <ConfigProvider theme={theme}>
      <OpNotificationProvider>
        <OpApp className="op-partner-center-app">
          <AlertWrapper />
          <OpAppScaffold
            appScopePrefix="partnerCenter"
            basename={basename}
            homePath={`${baseRoute}/${partnerDashboardRoute}`}
            /**
             * This will be removed in a follow-up
             * @see {@link https://jira.mot-solutions.com/browse/NEX-890}
             */
            isRedirectUrlEnabled={false}
            sidebarItems={sidebarItems({
              isVideoPartner,
              isAccessPartner,
            })}
            orgSwitcherProps={{
              visible: true,
              orgDataFilter: orgSwitcherFilter,
            }}
            title="Partner"
          >
            {/* Routes requiring an orgId should be placed inside this route */}
            <Route
              path={`${baseRoute}/`}
              element={
                <>
                  <OrgIdOutlet homePage={partnerDashboardRoute} />
                  <MigratedInfoModalWrapper />
                </>
              }
            >
              {/* All Routes that require login should be inside here */}
              <Route
                element={<OpAuthenticatedRoute isRedirectUrlEnabled={false} />}
              >
                <Route
                  element={<OpTermsGuard url={partnerTermsOfServiceRoute} />}
                >
                  <Route
                    path={partnerDashboardRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={homeMenuItem().scope ?? []}
                        element={<Dashboard />}
                        fallback={
                          <NotFoundPage
                            routeDescription={homeMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              homeMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path={partnerCustomersRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={customersMenuItem().scope ?? []}
                        element={<CustomersPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={customersMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              customersMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path={`${partnerCustomersRoute}/:id`}
                    element={
                      <OpScopeGuard
                        allowedScopes={customersMenuItem().scope ?? []}
                        element={<CustomerPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={customersMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              customersMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                  {isDmpEnabled && (
                    <Route
                      path={partnerBrandingRoute}
                      element={
                        <OpScopeGuard
                          allowedScopes={customersMenuItem().scope ?? []}
                          element={<BrandingPage />}
                          fallback={
                            <NotFoundPage
                              routeDescription={customersMenuItem().title}
                              missingScopeDescriptions={scopeCodesToDescriptions(
                                customersMenuItem().scope ?? [],
                              )}
                            />
                          }
                        />
                      }
                    />
                  )}
                  <Route
                    path={partnerSubscriptionRenewalsTableRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={renewalsMenuItem().scope ?? []}
                        element={<PartnerSubscriptionRenewalsTablePage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={renewalsMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              renewalsMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path={partnerRequestsTableRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={requestsMenuItem().scope ?? []}
                        element={<LicenseRequestsPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={requestsMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              requestsMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path={partnerBillingRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={billingMenuItem().scope ?? []}
                        element={<BillingPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={billingMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              billingMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path={partnerStoreRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={storeMenuItem().scope ?? []}
                        element={<StoreAccessPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={storeMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              storeMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerMailroomRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={mailroomMenuItem().scope ?? []}
                        element={<MailroomPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={mailroomMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              mailroomMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerAccessRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={
                          accessMenuItem(isAccessPartner).scope ?? []
                        }
                        element={<AccessPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={
                              accessMenuItem(isAccessPartner).title
                            }
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              accessMenuItem(isAccessPartner).scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerVideoRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={
                          videoMenuItem(isVideoPartner).scope ?? []
                        }
                        element={<VideoPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={
                              videoMenuItem(isVideoPartner).title
                            }
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              videoMenuItem(isVideoPartner).scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerVideoDeploymentsRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={videoDeploymentsMenuItem().scope ?? []}
                        element={<VideoDeploymentsPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={videoDeploymentsMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              videoDeploymentsMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerVisitorRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={visitorMenuItem().scope ?? []}
                        element={<VisitorPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={visitorMenuItem().title}
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              visitorMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerProtectRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={
                          protectMenuItem(isVideoPartner).scope ?? []
                        }
                        element={<ProtectPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={
                              protectMenuItem(isVideoPartner).title
                            }
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              protectMenuItem(isVideoPartner).scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />

                  <Route
                    path={partnerProtectDeploymentsRoute}
                    element={
                      <OpScopeGuard
                        allowedScopes={protectDeploymentsMenuItem().scope ?? []}
                        element={<ProtectDeploymentsPage />}
                        fallback={
                          <NotFoundPage
                            routeDescription={
                              protectDeploymentsMenuItem().title
                            }
                            missingScopeDescriptions={scopeCodesToDescriptions(
                              protectDeploymentsMenuItem().scope ?? [],
                            )}
                          />
                        }
                      />
                    }
                  />
                </Route>

                <Route
                  path={partnerTermsOfServiceRoute}
                  element={
                    <OpWithoutSidebar>
                      <TermsOfServicePage />
                    </OpWithoutSidebar>
                  }
                />

                <Route
                  path={partnerUsersRoute}
                  element={
                    <OpScopeGuard
                      allowedScopes={usersMenuItem().scope ?? []}
                      element={<UsersPage />}
                      fallback={
                        <NotFoundPage
                          routeDescription={usersMenuItem().title}
                          missingScopeDescriptions={scopeCodesToDescriptions(
                            usersMenuItem().scope ?? [],
                          )}
                        />
                      }
                    />
                  }
                />
              </Route>
            </Route>
          </OpAppScaffold>
        </OpApp>
      </OpNotificationProvider>
    </ConfigProvider>
  );
};
